import {
    createContext,
    FC,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react'
import { useSelector } from 'react-redux'
import { Consult } from 'app/models/domain/consult'
import { selectAuthState } from 'app/store/selectors'
import { useConsultList } from 'app/hooks/useConsultList'
import { useConsultEnrichment } from 'app/hooks/useConsultEnrichment'
import { logger } from 'app/logging/Logger'

export interface ConsultCacheContextValue {
    /** All cached consults, sorted by startDate descending */
    consults: Consult[]
    /** True once the initial listConsultDtos query has returned */
    isLoaded: boolean
    /** True when all consults in the current page set are fully enriched */
    isSynced: boolean
    /** True when a fetchNextPage call is in progress */
    isLoadingMore: boolean
    /** True when more pages are available from the server */
    hasMore: boolean
    /** Error state — currently unused with hook-based approach but kept for API compat */
    error: string | null
    /** Trigger pagination — delegates to useConsultList.fetchNextPage */
    fetchNextPage: () => void
    /** Lookup a single consult by ID from the cache. Returns undefined on miss. */
    getById: (consultId: string) => Consult | undefined
    /** Invalidate a cached consult's enrichment state and re-load entities/patient. Call after modifying entities or patient data. */
    invalidateConsult: (consultId: string) => Promise<void>
}

const ConsultCacheContext = createContext<ConsultCacheContextValue | undefined>(
    undefined
)

export const ConsultCacheProvider: FC<{ children: ReactNode }> = ({
    children,
}) => {
    const { currentUser } = useSelector(selectAuthState)
    const {
        consults,
        isSynced,
        isLoadingMore,
        hasMore,
        loadError,
        fetchNextPage,
    } = useConsultList(currentUser?.sub)
    const { reEnrich } = useConsultEnrichment()

    const [consultsState, setConsultsState] = useState<Consult[]>([])
    const consultMapRef = useRef<Map<string, Consult>>(new Map())

    // Sync consults from useConsultList into local state and rebuild lookup map
    useEffect(() => {
        setConsultsState(consults)
        const map = new Map<string, Consult>()
        consults.forEach((c) => map.set(c.id, c))
        consultMapRef.current = map
    }, [consults])

    const isLoaded = consults.length > 0 || isSynced

    const getById = useCallback((consultId: string) => {
        return consultMapRef.current.get(consultId)
    }, [])

    const invalidateConsult = useCallback(
        async (consultId: string) => {
            const consult = consultMapRef.current.get(consultId)
            if (!consult) return

            logger.info('[cache] Invalidating consult enrichment', {
                action: 'cache_invalidate_consult',
                metadata: { consultId },
            })

            try {
                await reEnrich(consult, { entities: true, patient: true })
            } catch (error) {
                logger.error(
                    'Failed to re-enrich invalidated consult',
                    error instanceof Error ? error : new Error(String(error)),
                    {
                        action: 'cache_invalidate_enrich_error',
                        metadata: { consultId },
                    }
                )
            }

            // Clone the enriched consult so getById returns a new reference.
            // Without this, useConsultSubscriptions' useEffect dependency on
            // cachedConsult won't detect the change (same object reference)
            // and enrichment in the detail view won't re-run.
            const cloned = Object.create(
                Object.getPrototypeOf(consult),
                Object.getOwnPropertyDescriptors(consult)
            ) as Consult
            consultMapRef.current.set(consultId, cloned)

            // Trigger re-render by emitting a new consults array with the clone
            setConsultsState((prev) =>
                prev.map((c) => (c.id === consultId ? cloned : c))
            )
        },
        [reEnrich]
    )

    return (
        <ConsultCacheContext.Provider
            value={{
                consults: consultsState,
                isLoaded,
                isSynced,
                isLoadingMore,
                hasMore,
                error: loadError
                    ? 'Failed to load consults. Please try refreshing.'
                    : null,
                fetchNextPage,
                getById,
                invalidateConsult,
            }}
        >
            {children}
        </ConsultCacheContext.Provider>
    )
}

export function useConsultCache(): ConsultCacheContextValue {
    const context = useContext(ConsultCacheContext)
    if (!context) {
        throw new Error(
            'useConsultCache must be used within a ConsultCacheProvider'
        )
    }
    return context
}
